import { MeetingDto } from '@interfaces/meetings/responses/meeting.response';
import {
  MeetingAudience,
  MeetingAudienceVisibilityDto,
  MeetingClientRestrictionKind,
  MeetingClientVisibilityFilter,
  MeetingVisibilityCountsDto,
  MeetingVisibilityDto,
} from '@interfaces/meetings/responses/meeting-visibility.response';

/**
 * Apresentação do título e da visibilidade das calls (card 3803). Funções puras, com spec isolado.
 *
 * Nada aqui decide regra: o estado efetivo de cada chave, o motivo e o que pode ser mexido vêm do
 * backend (`MeetingVisibilityDto`). Aqui só se escolhe ícone, cor e texto.
 */

/** Os quatro estados de um ícone da coluna "Quem vê" (protótipo, tela `lista`). */
export type AudienceIconTone = 'on' | 'off' | 'blocked' | 'pending';

/** Um ícone da coluna "Quem vê" (ou do selo "Visível para"). */
export interface AudienceIcon {
  audience: MeetingAudience;
  label: string;
  icon: string;
  tone: AudienceIconTone;
  /** Texto do `title`: "Cliente: barrada pelo Cog (dados de outros clientes)". */
  tooltip: string;
}

/** Nome de cada chave na tela. */
export const AUDIENCE_LABELS: Record<MeetingAudience, string> = {
  Team: 'Time',
  Partner: 'Parceiro',
  Client: 'Cliente',
};

/** Nome completo de cada chave na janela "Visibilidade da call". */
export const AUDIENCE_TITLES: Record<MeetingAudience, string> = {
  Team: 'Time Legatus',
  Partner: 'Parceiro',
  Client: 'Cliente',
};

/** Ícone padrão de cada chave (PrimeIcons). */
export const AUDIENCE_ICONS: Record<MeetingAudience, string> = {
  Team: 'pi pi-users',
  Partner: 'pi pi-briefcase',
  Client: 'pi pi-building',
};

export const AUDIENCE_ORDER: readonly MeetingAudience[] = ['Team', 'Partner', 'Client'];

/** Opções do filtro "Para o cliente", na ordem do protótipo. */
export const CLIENT_VISIBILITY_FILTER_LABELS: Record<MeetingClientVisibilityFilter, string> = {
  Released: 'Liberadas',
  BlockedByAi: 'Barradas pelo Cog',
  Private: 'Privadas',
  Locked: 'Internas ou sem empresa',
  Pending: 'Cog ainda avaliando',
};

export const CLIENT_VISIBILITY_FILTER_VALUES: readonly MeetingClientVisibilityFilter[] = [
  'Released',
  'BlockedByAi',
  'Private',
  'Locked',
  'Pending',
];

/** Motivo do Cog para barrar a call ao cliente, em português. */
export const CLIENT_RESTRICTION_LABELS: Record<MeetingClientRestrictionKind, string> = {
  OtherClientInformation: 'Informação de outro cliente',
  Profanity: 'Linguagem imprópria',
  ExplicitRestriction: 'Alguém pediu para não mostrar ao cliente',
  Other: 'Outro motivo',
};

/** Prefixo que o backend põe no motivo de uma chave travada ("Travada: call interna..."). */
const LOCKED_PREFIX = /^travada\s*[:.\-–]\s*/i;

/**
 * Motivo da chave como a janela mostra. A origem já diz "Travada pela regra", então o "Travada: " do
 * começo do motivo sai; na chave Cliente o resto vira "O cliente não vê: …" (o Parceiro já vem assim
 * do backend). Motivo sem o prefixo passa igual.
 */
export function displayReason(audience: MeetingAudience, reason: string): string {
  const text = (reason ?? '').trim();
  if (!LOCKED_PREFIX.test(text)) return text;
  const rest = text.replace(LOCKED_PREFIX, '');
  if (audience !== 'Client' || /^o cliente/i.test(rest)) return rest;
  return `O cliente não vê: ${rest.charAt(0).toLowerCase()}${rest.slice(1)}`;
}

/** O título que a tela mostra: o exibido do backend, ou "Meeting #N" quando vier vazio. */
export function displayTitle(meeting: Pick<MeetingDto, 'title' | 'sequentialId'>): string {
  return meeting.title?.trim() || `Meeting #${meeting.sequentialId}`;
}

/** Título do convite, só quando difere do exibido (linha de apoio da lista e "Título original" do detalhe). */
export function originalTitleIfDifferent(
  meeting: Pick<MeetingDto, 'title' | 'originalTitle'>
): string | null {
  const original = meeting.originalTitle?.trim();
  if (!original) return null;
  return original === (meeting.title ?? '').trim() ? null : original;
}

/** Tom do ícone de uma chave. A chave Cliente usa o `clientState` (barrada pelo Cog, Cog avaliando). */
export function audienceTone(
  visibility: MeetingVisibilityDto,
  audience: MeetingAudience
): AudienceIconTone {
  if (audience !== 'Client') return visibility[audienceKey(audience)].visible ? 'on' : 'off';
  switch (visibility.clientState) {
    case 'Released':
      return 'on';
    case 'BlockedByAi':
      return 'blocked';
    case 'Pending':
      return 'pending';
    default:
      return 'off';
  }
}

/** Os três ícones da coluna "Quem vê", com o motivo do backend no tooltip. */
export function audienceIcons(visibility: MeetingVisibilityDto | null | undefined): AudienceIcon[] {
  if (!visibility) return [];
  return AUDIENCE_ORDER.map(audience => {
    const tone = audienceTone(visibility, audience);
    const state = visibility[audienceKey(audience)];
    return {
      audience,
      label: AUDIENCE_LABELS[audience],
      icon: iconFor(audience, tone),
      tone,
      tooltip: `${AUDIENCE_LABELS[audience]}: ${state.reason}`,
    };
  });
}

/** Ícone do estado: barrada pelo Cog vira escudo, Cog avaliando vira ampulheta. */
function iconFor(audience: MeetingAudience, tone: AudienceIconTone): string {
  if (tone === 'blocked') return 'pi pi-shield';
  if (tone === 'pending') return 'pi pi-hourglass';
  return AUDIENCE_ICONS[audience];
}

/** Campo do DTO de cada chave. */
export function audienceKey(audience: MeetingAudience): 'team' | 'partner' | 'client' {
  return audience === 'Team' ? 'team' : audience === 'Partner' ? 'partner' : 'client';
}

/** A chave de uma audiência no DTO. */
export function audienceState(
  visibility: MeetingVisibilityDto,
  audience: MeetingAudience
): MeetingAudienceVisibilityDto {
  return visibility[audienceKey(audience)];
}

/** Quantas calls a pílula "Para o cliente" mostra; `null` = todas. */
export function countForFilter(
  counts: MeetingVisibilityCountsDto | null,
  filter: MeetingClientVisibilityFilter | null
): number | null {
  if (!counts) return null;
  switch (filter) {
    case null:
      return counts.all;
    case 'Released':
      return counts.released;
    case 'BlockedByAi':
      return counts.blockedByAi;
    case 'Private':
      return counts.private;
    case 'Locked':
      return counts.locked;
    default:
      return counts.pending;
  }
}

/**
 * "Renomeada por Victor Tassinari em 28/10" (o `dd/MM` vem formatado pelo chamador, que tem o
 * `DateFormatterService`). Sem título dado por pessoa, nulo.
 */
export function renamedByText(
  meeting: Pick<MeetingDto, 'customTitle' | 'customTitleByName'>,
  renamedOn: string | null
): string | null {
  if (!meeting.customTitle) return null;
  const who = meeting.customTitleByName ? ` por ${meeting.customTitleByName}` : '';
  const when = renamedOn ? ` em ${renamedOn}` : '';
  return `Renomeada${who}${when}`;
}

/** Formata segundos como `mm:ss` (ou `h:mm:ss`) para o trecho apontado pelo Cog. */
export function formatSecondsAsClock(seconds: number | null | undefined): string | null {
  if (seconds === null || seconds === undefined || seconds < 0) return null;
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}
